import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, X, DollarSign, Package, Trophy } from 'lucide-react';

// Tipos de logro para personalizar ícono y color
export type CelebrationLogro = {
  tipo: 'stock_agotado' | 'todo_pagado' | 'todo_entregado';
  texto: string;
};

interface CelebrationAlertProps {
  // Título principal del aviso
  title: string;
  // Lista de logros alcanzados (se muestran todos en un solo aviso)
  logros: CelebrationLogro[];
  duration?: number;
  onClose: () => void;
}

// Configuración visual por tipo de logro
const logroConfig: Record<CelebrationLogro['tipo'], { icon: React.ElementType; color: string }> = {
  stock_agotado: { icon: Package, color: 'text-emerald-600' },
  todo_pagado: { icon: DollarSign, color: 'text-blue-600' },
  todo_entregado: { icon: CheckCircle, color: 'text-violet-600' },
};

export const CelebrationAlert: React.FC<CelebrationAlertProps> = ({
  title,
  logros,
  duration = 6000,
  onClose
}) => {
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const elapsedRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const pausedRef = useRef(false);

  useEffect(() => {
    // Limpiar interval previo si existe (React Strict Mode)
    if (intervalRef.current) clearInterval(intervalRef.current);
    elapsedRef.current = 0;
    lastTickRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const now = Date.now();
      elapsedRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      const remaining = Math.max(0, 100 - (elapsedRef.current / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsExiting(true);
        setTimeout(() => onCloseRef.current(), 400);
      }
    }, 50);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [duration]);

  const handleMouseEnter = () => {
    pausedRef.current = true;
  };

  const handleMouseLeave = () => {
    pausedRef.current = false;
    lastTickRef.current = Date.now();
  };

  const handleClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsExiting(true);
    setTimeout(() => onClose(), 400);
  };

  // Determinar ícono principal según la cantidad y tipo de logros
  const esMultiple = logros.length > 1;
  const MainIcon = esMultiple ? Trophy : logroConfig[logros[0]?.tipo]?.icon || CheckCircle;
  const mainBgColor = esMultiple ? 'bg-amber-100' : (
    logros[0]?.tipo === 'todo_pagado' ? 'bg-blue-100' :
    logros[0]?.tipo === 'todo_entregado' ? 'bg-violet-100' : 'bg-emerald-100'
  );
  const mainIconColor = esMultiple ? 'text-amber-600' : (logroConfig[logros[0]?.tipo]?.color || 'text-emerald-600');
  const barColor = esMultiple ? 'bg-amber-500' : (
    logros[0]?.tipo === 'todo_pagado' ? 'bg-blue-500' :
    logros[0]?.tipo === 'todo_entregado' ? 'bg-violet-500' : 'bg-emerald-500'
  );
  const borderColor = esMultiple ? 'border-amber-200' : (
    logros[0]?.tipo === 'todo_pagado' ? 'border-blue-200' :
    logros[0]?.tipo === 'todo_entregado' ? 'border-violet-200' : 'border-emerald-200'
  );

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-none">
      {/* Backdrop tenue para que la celebración no pase desapercibida; no bloquea
          (pointer-events-none): la tarjeta sigue siendo el único elemento interactivo. */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isExiting ? 'opacity-0' : 'opacity-100 animate-in fade-in-0'
        }`}
      />
      <div
        onClick={handleClose}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative pointer-events-auto w-full max-w-sm bg-white rounded-2xl shadow-2xl border ${borderColor} overflow-hidden cursor-pointer transition-all duration-400 ${
          isExiting
            ? 'opacity-0 scale-90'
            : 'opacity-100 scale-100 animate-in zoom-in-95 fade-in'
        }`}
      >
        {/* Contenido */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="flex justify-center mb-3">
            <div className={`w-12 h-12 rounded-full ${mainBgColor} flex items-center justify-center`}>
              <MainIcon className={`w-7 h-7 ${mainIconColor}`} />
            </div>
          </div>
          <p className="text-base font-bold text-slate-800">{title}</p>

          {/* Lista de logros */}
          <div className={`mt-3 space-y-1.5 ${logros.length === 1 ? '' : 'text-left'}`}>
            {logros.map((logro, i) => {
              const config = logroConfig[logro.tipo];
              const Icon = config.icon;
              return (
                <div key={i} className={`flex items-center gap-2 ${logros.length === 1 ? 'justify-center' : ''}`}>
                  {logros.length > 1 && <Icon className={`w-4 h-4 flex-shrink-0 ${config.color}`} />}
                  <span className="text-sm text-slate-600">{logro.texto}</span>
                </div>
              );
            })}
          </div>

          {/* Botón cerrar */}
          <button
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
            className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Barra de progreso */}
        <div className="h-1 bg-slate-100">
          <div
            className={`h-full ${barColor} transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};
